import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Calendar, MapPin, User, FileText, Upload } from 'lucide-react';
import { msg } from '../utils/messages';
import { MultiSelectFilter } from './MultiSelectFilter';
import IntervaloAgendamentoModal from './IntervaloAgendamentoModal';
import { useJobForm } from '../hooks/useJobForm';
import useAuthStore from '../stores/authStore';
import { computeHoraFim59min } from '../utils/duracao';

interface JobFormModalProps {
  job?: any | null;
  onClose: (shouldReload: boolean) => void;
  onSuccess: (message: string) => void;
}

export default function JobFormModal({ job, onClose, onSuccess }: JobFormModalProps) {
  const titleRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthStore();

  const {
    formData,
    setFormData,
    hospitals,
    clientes,
    locations,
    operators,
    selectedFile,
    setSelectedFile,
    loading,
    loadingData,
    errors,
    setErrors,
    showIntervaloModal,
    intervaloValidacoes,
    intervaloLoading,
    allHospitalLocais,
    selectedLocalIds,
    handleLocaisChange,
    loadingLocais,
    calcularDuracao,
    handleChange,
    handleSubmit,
    handleIntervaloConfirm,
    handleIntervaloConfirmWithJustificativas,
    handleIntervaloCancel,
  } = useJobForm({ job, onClose, onSuccess });

  const isMaster = user?.role === 'MASTER';
  const isFinalized = job && ['EXECUTADA', 'CANCELADA', 'NAO_EXECUTADA'].includes(job.status);
  const statusDisabled = loading || (!!isFinalized && !isMaster);

  if (loadingData) {
    return (
      <Transition appear show={true} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => onClose(false)}>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Carregando dados...</p>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>
    );
  }

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => onClose(false)}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-2xl transform overflow-visible rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-2xl font-bold text-gray-900 dark:text-white">
                    {job ? 'Editar Contagem' : 'Nova Contagem'}
                  </Dialog.Title>
                  <button
                    onClick={() => onClose(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* LINHA 1 - Data, Hora Início, Hora Fim, Status, Clientes */}
                  <div className="grid grid-cols-5 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Data *
                      </label>
                      <input
                        type="date"
                        name="data_prevista"
                        value={formData.data_prevista}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                          errors.data_prevista ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                      />
                      {errors.data_prevista && <p className="text-red-500 text-xs mt-1">{errors.data_prevista}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Início *
                      </label>
                      <input
                        type="time"
                        name="hora_inicio"
                        value={formData.hora_inicio}
                        onChange={(e) => {
                          // Ao alterar hora_inicio, auto-calcula hora_fim = +29min (padrao).
                          // Master pode sobrescrever a Hora Final manualmente em seguida.
                          handleChange(e);
                          const novoFim = computeHoraFim59min(e.target.value);
                          setFormData((prev: any) => ({ ...prev, hora_fim: novoFim }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Fim {isMaster ? '*' : '(automatico — 29 min)'}
                      </label>
                      <input
                        type="time"
                        name="hora_fim"
                        value={formData.hora_fim}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                          !isMaster ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                        disabled={loading || !isMaster}
                        title={!isMaster ? 'Apenas Master pode editar a Hora Final. Duracao padrao da contagem: 29 minutos.' : undefined}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Status
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        disabled={statusDisabled}
                      >
                        <option value="PRE_AGENDADA">Pré-Agendada</option>
                        <option value="AGENDADA">Agendada</option>
                        <option value="REAGENDADA">Reagendada</option>
                        <option value="CONFIRMADA">Confirmada</option>
                        <option value="EM_EXECUCAO">Em Execução</option>
                        <option value="EXECUTADA">Executada</option>
                        <option value="NAO_EXECUTADA">Não Executada</option>
                        <option value="CANCELADA">Cancelada</option>
                      </select>
                    </div>

                    <div>
                      <MultiSelectFilter
                        label="Clientes *"
                        options={clientes.map(c => ({ value: c.id, label: c.nome_fantasia || c.razao_social }))}
                        selectedValues={formData.cliente_ids}
                        onChange={(values) => {
                          setFormData(prev => ({ ...prev, cliente_ids: values }));
                          if (errors.cliente_ids) {
                            setErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.cliente_ids;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder="Selecione"
                        showTodos={false}
                        showBadges={false}
                      />
                      {errors.cliente_ids && <p className="text-red-500 text-xs mt-1">{errors.cliente_ids}</p>}
                    </div>
                  </div>

                  {/* Duração estimada */}
                  {formData.hora_inicio && formData.hora_fim && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 -mt-2">
                      Duração estimada: <span className="font-medium">{calcularDuracao()}</span>
                    </div>
                  )}

                  {/* Hospital */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Estabelecimento *
                    </label>
                    <select
                      name="hospital_id"
                      value={formData.hospital_id}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.hospital_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={loading}
                    >
                      <option value="">Selecione um estabelecimento</option>
                      {hospitals.map((hospital) => (
                        <option key={hospital.id} value={hospital.id}>
                          {hospital.nome_fantasia} - CNPJ: {hospital.cnpj} - Código: {hospital.codigo}
                        </option>
                      ))}
                    </select>
                    {errors.hospital_id && <p className="text-red-500 text-sm mt-1">{errors.hospital_id}</p>}
                  </div>

                  {/* Localização */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Localização *
                    </label>
                    <select
                      name="location_id"
                      value={formData.location_id}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.location_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={loading || !formData.hospital_id || locations.length === 0}
                    >
                      <option value="">
                        {!formData.hospital_id
                          ? 'Selecione um estabelecimento primeiro'
                          : locations.length === 0
                          ? 'Nenhuma localização disponível'
                          : 'Selecione uma localização'}
                      </option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.nome}
                        </option>
                      ))}
                    </select>
                    {errors.location_id && <p className="text-red-500 text-sm mt-1">{errors.location_id}</p>}
                  </div>

                  {/* Pontos de Contagem (editável) */}
                  {formData.hospital_id && (
                    <div>
                      <MultiSelectFilter
                        label={`Pontos de Contagem${loadingLocais ? ' (carregando...)' : ''}`}
                        options={allHospitalLocais.map(l => ({
                          value: l.id,
                          label: `${l.nome}${l.responsavel ? ` (${l.responsavel})` : ''}`,
                        }))}
                        selectedValues={selectedLocalIds}
                        onChange={(values) => handleLocaisChange(values.map(Number))}
                        placeholder="Selecione pontos de contagem"
                        showBadges={true}
                        showTodos={true}
                        disabled={loading}
                      />
                    </div>
                  )}

                  {/* Operador */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <User className="w-4 h-4 inline mr-1" />
                      Operador *
                    </label>
                    <select
                      name="operador_id"
                      value={formData.operador_id}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.operador_id ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={loading}
                    >
                      <option value="">Selecione um operador</option>
                      {operators.map((operator) => (
                        <option key={operator.id} value={operator.id}>
                          {operator.name} ({operator.email})
                        </option>
                      ))}
                    </select>
                    {errors.operador_id && <p className="text-red-500 text-sm mt-1">{errors.operador_id}</p>}
                  </div>

                  {/* Título */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Título *
                    </label>
                    <input
                      ref={titleRef}
                      type="text"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleChange}
                      placeholder="Ex: Contagem Mensal Abril 2024"
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        errors.titulo ? 'border-red-500' : 'border-gray-300'
                      }`}
                      disabled={loading}
                    />
                    {errors.titulo && <p className="text-red-500 text-sm mt-1">{errors.titulo}</p>}
                  </div>

                  {/* Upload Excel (only on create) */}
                  {!job && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <Upload className="w-4 h-4 inline mr-1" />
                        Upload Planilha Excel (Opcional)
                      </label>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400"
                        disabled={loading}
                      />
                      {selectedFile && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Arquivo selecionado: {selectedFile.name}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Observações */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      <FileText className="w-4 h-4 inline mr-1" />
                      Observações
                    </label>
                    <textarea
                      name="descricao"
                      value={formData.descricao}
                      onChange={handleChange}
                      placeholder="Observações adicionais sobre a contagem..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      disabled={loading}
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => onClose(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      disabled={loading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                    >
                      {loading ? 'Salvando...' : job ? 'Salvar' : 'Criar Contagem'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Epic 46/47: Modal de Validação de Intervalo */}
      <IntervaloAgendamentoModal
        isOpen={showIntervaloModal}
        onClose={handleIntervaloCancel}
        onConfirm={handleIntervaloConfirm}
        onConfirmWithJustificativas={handleIntervaloConfirmWithJustificativas}
        validacoes={intervaloValidacoes}
        dataSelecionada={formData.data_prevista}
        loading={intervaloLoading}
      />
    </Transition>
  );
}
